/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Icon, Tooltip } from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import { FaCamera } from 'react-icons/fa';

const ImageUploadCard = ({
  selectedImage,
  imagePreview,
  onImageSelect,
  onImageChange,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      onImageSelect(file, e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const renderUploadArea = () => (
    <div
      style={{
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: '60px 30px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        width: '100%',
        maxWidth: '400px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={() => fileInputRef.current?.click()}
    >
      <FaCamera size={48} style={{ color: '#666', marginBottom: '16px' }} />
      <h3>
        <FormattedMessage
          id="ui-plugin-inventory-templates.imageExtraction.uploadTitle"
          defaultMessage="Upload Book Cover Image"
        />
      </h3>
      <p>
        <FormattedMessage
          id="ui-plugin-inventory-templates.imageExtraction.uploadDescription"
          defaultMessage="Drag and drop an image here, or click to select"
        />
      </p>
      <p style={{ fontSize: '12px', color: '#666' }}>
        <FormattedMessage
          id="ui-plugin-inventory-templates.imageExtraction.supportedFormats"
          defaultMessage="Supported formats: JPG, PNG, WEBP (Max 10MB)"
        />
      </p>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{ display: 'none' }}
      />
    </div>
  );

  return (
    <Card
      headerStart={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <strong>
            <FormattedMessage
              id="ui-plugin-inventory-templates.imageExtraction.uploadedImage"
              defaultMessage="Uploaded Image"
            />
          </strong>
          {selectedImage && (
            <span style={{ fontSize: '12px', color: '#666' }}>
              ({selectedImage.name})
            </span>
          )}
        </div>
      }
      cardStyle="positive"
      headerEnd={
        selectedImage ? (
          <Tooltip
            id="change-image-tooltip"
            text={
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.changeImage"
                defaultMessage="Change Image"
              />
            }
          >
            {({ ref, ariaIds }) => (
              <Button
                ref={ref}
                aria-labelledby={ariaIds.text}
                buttonStyle="slim"
                onClick={onImageChange}
                bottomMargin0
              >
                <Icon icon="replace" size="large" />
              </Button>
            )}
          </Tooltip>
        ) : null
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          overflow: 'hidden',
          minHeight: '0',
        }}
      >
        {!selectedImage ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {renderUploadArea()}
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={imagePreview}
              alt="Book cover"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

ImageUploadCard.propTypes = {
  selectedImage: PropTypes.object,
  imagePreview: PropTypes.string,
  onImageSelect: PropTypes.func.isRequired,
  onImageChange: PropTypes.func.isRequired,
};

export default ImageUploadCard;
